/**
 * Die Personenauswahl im Sheet „Zugriff geben" (`access-add-user`, Spec 05a,
 * „Sheet Zugriff am Objekt": „eine Personenauswahl (`access-add-user`) statt
 * E-Mail"). Sucht seit AK-381 wie `TenantPicker` über `GET /access/users?q=`
 * **serverseitig** über Name und E-Mail — ein Client-Filter über die geladene
 * erste Seite fand einen Treffer auf Seite 2 nicht (K-B1, `13-gatekeeper.md`,
 * gemessen bei 56 Personen gegen `DEFAULT_PAGE_SIZE = 50`). Lädt wie
 * `TenantPicker` nicht vor, solange kein Suchtext steht.
 *
 * Findet die Suche niemanden, führt `access-add-invite` ins Einladen-Sheet
 * (Spec: „Ist die gesuchte Person noch nicht da …").
 */

import { useState } from 'react'

import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { useAccessUsers } from '@/features/access/api'
import { FIELD_CLASS } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'

export interface PersonSelection {
  readonly id: string
  readonly label: string
}

interface PersonPickerProps {
  readonly testId: string
  readonly value: PersonSelection | null
  readonly onSelect: (person: PersonSelection) => void
  /** „Ändern" — öffnet die Suche wieder, ohne `onSelect` mit einem
   * Platzhalterwert zu missbrauchen. */
  readonly onClear: () => void
  readonly onRequestInvite: () => void
  /** Wer hier schon eine direkte Zeile hat, wird nicht noch einmal
   * vorgeschlagen — reine Vorauswahl-Bequemlichkeit, kein zweiter Grant wäre
   * ohnehin serverseitig `409 grant_exists`. */
  readonly excludeIds: readonly string[]
  readonly error: FieldError | undefined
  readonly errorPrefix: string
  /** Gesetzt für `object-private-grant-self` (Spec 05b, „Wenn 409
   * would_lock_out kommt"): Die Person steht schon fest (man selbst), „Ändern"
   * entfällt — dieselbe Sperre wie `disabled` in `EntityPicker`. */
  readonly disabled?: boolean
}

const API_FIELD = 'user_id'

export function PersonPicker({
  testId,
  value,
  onSelect,
  onClear,
  onRequestInvite,
  excludeIds,
  error,
  errorPrefix,
  disabled = false,
}: PersonPickerProps) {
  const [q, setQ] = useState('')
  const trimmed = q.trim()
  const usersQuery = useAccessUsers(trimmed, { enabled: trimmed !== '' })
  const hasError = error?.field === API_FIELD
  const errorTestId = `${errorPrefix}-error-${API_FIELD}`

  // Der Server filtert bereits über `q` (AK-381) — hier bleibt nur die
  // Auslese bereits direkt zugriffsberechtigter Personen, kein Textfilter.
  const options = (usersQuery.data?.items ?? []).filter((person) => !excludeIds.includes(person.id))

  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <span className="text-label text-muted">Person</span>

      {value !== null ? (
        <div className={`flex items-center justify-between gap-2 ${FIELD_CLASS}`}>
          <span className="text-text">{value.label}</span>
          {!disabled && (
            <button
              type="button"
              data-testid={`${testId}-change`}
              onClick={onClear}
              className="text-accent text-label inline-flex min-h-(--tap-min) min-w-(--tap-min) items-center justify-center hover:underline"
            >
              Ändern
            </button>
          )}
        </div>
      ) : (
        <>
          <input
            data-testid={`${testId}-search`}
            type="search"
            aria-label="Person suchen"
            placeholder="Person suchen"
            value={q}
            aria-invalid={hasError}
            aria-describedby={hasError ? errorTestId : undefined}
            onChange={(event) => {
              setQ(event.target.value)
            }}
            className={`text-text ${FIELD_CLASS}`}
          />

          {trimmed !== '' && usersQuery.isPending && <SkeletonRows testId={`${testId}-loading`} rows={3} />}

          {trimmed !== '' && usersQuery.isError && (
            <ErrorState
              testId={`${testId}-error`}
              retryTestId={`${testId}-retry`}
              error={usersQuery.error}
              onRetry={() => {
                void usersQuery.refetch()
              }}
            />
          )}

          {trimmed !== '' && usersQuery.isSuccess && options.length === 0 && (
            <EmptyState testId={`${testId}-empty`} title="Keine Person gefunden." />
          )}

          {trimmed !== '' && usersQuery.isSuccess && options.length > 0 && (
            <ul className="border-border rounded-card border">
              {options.map((person) => (
                <li key={person.id} className="border-border border-b last:border-b-0">
                  <button
                    type="button"
                    data-testid={`${testId}-option-${person.id}`}
                    onClick={() => {
                      onSelect({ id: person.id, label: person.display_name ?? person.email })
                    }}
                    className="hover:bg-bg flex min-h-(--tap-min) w-full flex-col items-start justify-center gap-0.5 px-3 py-1 text-left transition-colors duration-(--duration-fast) ease-(--ease-out)"
                  >
                    <span className="text-body text-text">{person.display_name ?? person.email}</span>
                    <span className="text-label text-muted">{person.email}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button
            type="button"
            data-testid="access-add-invite"
            onClick={onRequestInvite}
            className="text-accent text-label self-start hover:underline"
          >
            Person einladen
          </button>
        </>
      )}

      {hasError && (
        <p id={errorTestId} data-testid={errorTestId} role="alert" className="text-status-overdue">
          {error.message}
        </p>
      )}
    </div>
  )
}
